import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Button, Space} from 'antd';
import {
  StyledTableFilters,
  StyledTableFiltersCardSelect,
  StyledTableFiltersDivider,
  StyledTableFiltersSearch,
} from './StyledTableFilters';
import AddPaymentWithBankingCheck from '../AddPaymentWithBankingCheck';
import ExportModal from '../ExportModal';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow/AuthenticationWindow';
import {useIsMount} from '../../../../hooks';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {transactionActions} from '../../../../state/actions/transaction.actions';
import {formHelpers, objectHelpers, scaHelpers, systemHelpers, transactionsHelpers} from '../../../../utils/helpers';
import {cardPaymentStatusesConstants, DEFAULT_MODAL_PROPS, SCAActionsConstants} from '../../../../constants';
import {firebaseEvents} from '../../../../snippets/firebase';

const {logEvent} = systemHelpers;

const SCA_OPERATION_NAME = SCAActionsConstants.TRANSACTIONS_DOWNLOAD_PDF_AND_CSV_ACTION;

const paymentOptions = {
  ALL: 'all',
  UNMATCHED: 'unmatched',
}
const {ALL, UNMATCHED} = paymentOptions;

const exportTimeFormat = 'YYYY-MM-DD';

const getPaymentStatusValue = (value) => {
  const isUnmatched = value === UNMATCHED;
  const paymentStatus = value === ALL || isUnmatched ? [] : [value];
  const amountGte = isUnmatched ? 0.01 : undefined;
  const hasInvoice = isUnmatched ? false : undefined;
  const paymentStatusNotIn = isUnmatched
    ? transactionsHelpers.getDeclinedTransactionStatuses()
    : [];

  return {
    amount_gte: amountGte,
    has_invoice: hasInvoice,
    payment_status: paymentStatus,
    payment_status_not_in: paymentStatusNotIn,
  };
};

const getPaymentStatusFieldValue = (filter) => {
  let hasInvoice = filter?.has_invoice;
  const paymentStatus = filter?.payment_status;
  let value = String(hasInvoice) === String(false) ? UNMATCHED : Array.isArray(paymentStatus) ? paymentStatus[0] : paymentStatus;
  return value || ALL;
}

const validateDateRange = (dateRange) => dateRange && dateRange.length === 2;

const selectProps = {
  allowClear: true,
  maxTagCount: 1,
  mode: 'multiple',
  size: 'large',
  showSearch: true
}

const TableFilters = ({
  enableExport = true,
  onFilter,
  searchParams,

  cards,
  employees,
  employeeEmail,
  isAdmin,
  isKnownEmployee,

  downloadTransactions,
  downloadZipInvoices,
}) => {
  const [t] = useTranslation(['main', 'cards', 'transactions']);
  const [addPaymentModalProps, setAddPaymentModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [authWindowProps, setAuthWindowProps] = useState(DEFAULT_MODAL_PROPS);
  const [exportDetails, setExportDetails] = useState({range: undefined, format: null});
  const [exportModalProps, setExportModalProps] = useState(DEFAULT_MODAL_PROPS);

  const [filter, setFilter] = useState({
    card_id_in: [],
    has_invoices: undefined,
    payment_status: undefined,
    search: '',
    user_id_in: []
  });
  const isMounted = !useIsMount();

  useEffect(() => {
    if (isKnownEmployee) {
      if (searchParams) {
        setFilter(objectHelpers.getSearchParams(searchParams));
      } else {
        handleFilter(filter);
      }
    }
  }, [isKnownEmployee]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    isMounted && handleFilter(filter);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const cardOptions = useMemo(() => formHelpers.getCardsOptions({cards}), [cards]);

  const employeeOptions = useMemo(() =>
    formHelpers.getEmployeeOptions({employees, employeeEmail, t, valueKey: 'employee_id'}), [employees, employeeEmail, t]);

  const paymentStatusOptions = [
    {label: t('all'), value: ALL},
    {label: t('missingInvoices'), value: UNMATCHED},
    {label: t('cardPaymentStatuses.declined'), value: cardPaymentStatusesConstants.DECLINED}
  ];

  const paymentStatusValue = useMemo(() => getPaymentStatusFieldValue(filter), [filter]);

  const blockedExportFormat = useMemo(() => paymentStatusValue === ALL ? undefined : 'zip', [paymentStatusValue]);

  const handleFilter = (filterData) => onFilter && onFilter(filterData);

  const handleOnFilter = (key, value) => setFilter({...filter, [key]: value});

  const handleChangePaymentStatus = (value) => {
    setFilter({
      ...filter,
      ...getPaymentStatusValue(value)
    });
  }

  const handleExportErrorCallback = () => setExportModalProps({...exportModalProps, open: true, loading: false, error: t('fileIsTooLargeToDownloadMessage')});

  const handleExportZip = (data) => {
    const {date_range: dateRange} = data;

    const isValidDateRange = validateDateRange(dateRange);

    if (!isValidDateRange) return;

    const userEmails = [].concat(filter?.user_id_in || [])
      .map(id => employees.find(e => e.employee_id === id)?.email)
      .filter(Boolean);

    const query = {
      date_gte: dateRange[0].format(exportTimeFormat),
      date_lte: dateRange[1].format(exportTimeFormat),
      user_email_in: userEmails,
      ...objectHelpers.removeKeysFromObject(filter, ['has_invoices', 'payment_status', 'user_id_in'])
    };

    downloadZipInvoices({
      query,
      successCallback: (data) => {
        window.open(data.url, 'download');
        handleCancelExportModal();
        logEvent(firebaseEvents.TRANSACTIONS_DOWNLOAD_ZIP_INVOICES);
      },
      errorCallback: handleExportErrorCallback
    });
  }

  const handleExportTransactions = (data) => {
    const {format, date, date_range: dateRange} = data;

    const isValidDateRange = validateDateRange(dateRange);

    const [startDate, endDate] = format === 'csv' && isValidDateRange
      ? dateRange
      : [date.startOf('month'), date.endOf('month')];

    const query = {
      date_gte: startDate.format(exportTimeFormat),
      date_lte: endDate.format(exportTimeFormat),
      file_format: format,
      ...(format === 'csv' && filter)
    };

    const downloadLogEvents = {
      csv: firebaseEvents.TRANSACTIONS_EXPORT_CSV,
      pdf: firebaseEvents.TRANSACTIONS_EXPORT_PDF
    };

    const downloadLogEvent = downloadLogEvents[format];

    downloadTransactions({
      headers: scaHelpers.getAuthHeaders(SCA_OPERATION_NAME),
      query,
      successCallback: (data) => {
        window.open(data.url, 'download');
        handleCancelExportModal();
      },
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => {
            setAuthWindowProps({...authWindowProps, ...scaProps});
            setExportDetails(data);
            downloadLogEvent && logEvent(downloadLogEvent);
          },
          errorCallback: handleExportErrorCallback
        });
      }
    });
  }

  const handleOkExportModal = (data) => {
    setExportModalProps({...exportModalProps, loading: true});

    const exportHandler = data.format === 'zip' ? handleExportZip : handleExportTransactions;
    exportHandler(data);
  }

  const handleOnExportClick = () => setExportModalProps({...exportModalProps, open: true});

  const handleCancelExportModal = () => setExportModalProps({...exportModalProps, open: false, loading: false, error: null});

  const handleCloseAuthModal = () => {
    setAuthWindowProps({...authWindowProps, open: false});
    setExportModalProps({...exportModalProps, loading: false});
  }

  const handleOnSuccessAuth = () => {
    handleCloseAuthModal();
    handleOkExportModal(exportDetails);
  }

  const handleOpenAddPaymentModal = () => setAddPaymentModalProps({...addPaymentModalProps, open: true});

  const handleCancelAddPaymentModal = () => setAddPaymentModalProps(DEFAULT_MODAL_PROPS);

  return (
    <StyledTableFilters align='center'>
      <Space align='center' size='small'>
        <StyledTableFiltersSearch
          allowClear
          onSearch={(value) => handleOnFilter('search', value)}
          placeholder={t('search')}
          value={filter.search}
          size='large'
        />
        <StyledTableFiltersCardSelect
          {...selectProps}
          onChange={(value) => handleOnFilter('card_id_in', value)}
          optionFilterProp='label'
          optionLabelProp='optionLabel'
          options={cardOptions}
          placeholder={t('transactions:tableFilters.placeholder.cards')}
          value={filter?.card_id_in ?? []}
        />
        {isAdmin && (
          <StyledTableFiltersCardSelect
            {...selectProps}
            styles={{ popup: { root: {minWidth: 235} } }}
            onChange={(value) => handleOnFilter('user_id_in', value)}
            optionFilterProp='optionLabel'
            optionLabelProp='optionLabel'
            options={employeeOptions}
            placeholder={t('cards:tableFilters.placeholder.users')}
            value={filter?.user_id_in ?? []}
          />
        )}
        <StyledTableFiltersCardSelect
          onChange={handleChangePaymentStatus}
          options={paymentStatusOptions}
          size='large'
          value={paymentStatusValue}
        />
      </Space>
      {(isAdmin && enableExport) && (
        <>
          <Space size={0}>
            <StyledTableFiltersDivider type='vertical' />
            <Space size='small'>
              <Button
                onClick={handleOpenAddPaymentModal}
                size='large'
                type='primary'
              >
                + {t('payment')}
              </Button>
              <Button
                onClick={handleOnExportClick}
                size='large'
              >
                {t('export')}
              </Button>
            </Space>
          </Space>

          <ExportModal
            {...exportModalProps}
            blockedFormat={blockedExportFormat}
            handleCancel={handleCancelExportModal}
            handleOk={handleOkExportModal}
            title={`${t('export')} ${t('transactions')}`}
          />

          <AddPaymentWithBankingCheck
            modalProps={{
              ...addPaymentModalProps,
              onCancel: handleCancelAddPaymentModal,
            }}
          />

          <AuthenticationWindow
            {...authWindowProps}
            handleCancel={handleCloseAuthModal}
            onSuccess={handleOnSuccessAuth}
            operationName={SCA_OPERATION_NAME}
          />
        </>
      )}

    </StyledTableFilters>
  );
}

TableFilters.propTypes = {
  enableExport: PropTypes.bool,
  onFilter: PropTypes.func,
  searchParams: PropTypes.object,
}

const mapStateToProps = ({ user: { employee, isKnownEmployee, isAdmin }, card: { cardsSummaries }, company: { employees } }) => ({
  cards: cardsSummaries,
  employees,
  employeeEmail: employee?.email ?? null,
  isKnownEmployee,
  isAdmin
});

const mapDispatchToProps = {
  downloadTransactions: transactionActions.downloadTransactions,
  downloadZipInvoices: expenseActions.downloadZipInvoices
}

export default connect(mapStateToProps, mapDispatchToProps)(TableFilters);
